import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon
} from '@mui/icons-material';

interface TenantUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'viewer' | 'user';
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  tenant_id: string;
}

interface TenantUsersProps {
  tenantId?: string;
}

function TenantUsers({ tenantId }: TenantUsersProps) {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserName, setAddUserName] = useState('');
  const [addUserPassword, setAddUserPassword] = useState('');
  const [addUserRole, setAddUserRole] = useState<'admin' | 'manager' | 'viewer'>('viewer');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadUsers();
  }, [tenantId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // apiServiceを使用してtenant_users_v2から取得
      const { apiService } = await import('../services/api');
      
      const response = await apiService.getTenantUsers({
        tenant_id: tenantId,
        limit: 1000,
        offset: 0
      }) as any;
      
      if (response?.success && response?.data) {
        // データをフィルタリング（現在のテナントのユーザーのみ）
        const filteredUsers = tenantId 
          ? response.data.filter((user: any) => user.tenant_id === tenantId)
          : response.data;
        
        setUsers(filteredUsers);
      } else {
        setUsers([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load users:', error);
      setMessage({ type: 'error', text: 'ユーザー一覧の読み込みに失敗しました' });
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.toggleTenantUserStatus(userId) as any;
      
      if (response.success) {
        // ユーザー一覧を再読み込み
        await loadUsers();
        setMessage({ 
          type: 'success', 
          text: response.message || `ユーザーを${!currentStatus ? 'アクティブ' : '非アクティブ'}にしました` 
        });
      } else {
        throw new Error(response.error || 'ステータス変更に失敗しました');
      }
    } catch (error: any) {
      console.error('Failed to toggle user status:', error);
      setMessage({ type: 'error', text: error.message || 'ユーザーステータスの変更に失敗しました' });
    }
  };

  const handleAddUser = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'テナントIDが指定されていません' });
      return;
    }

    try {
      const { apiService } = await import('../services/api');
      
      const response = await apiService.createTenantUser(tenantId, {
        email: addUserEmail,
        name: addUserName,
        password: addUserPassword,
        role: addUserRole
      }) as any;
      
      if (response.success) {
        setMessage({ type: 'success', text: 'ユーザーを追加しました' });
        setAddUserDialogOpen(false);
        setAddUserEmail('');
        setAddUserName('');
        setAddUserPassword('');
        setAddUserRole('viewer');
        // ユーザー一覧を再読み込み
        await loadUsers();
      } else {
        throw new Error(response.error || 'ユーザー追加に失敗しました');
      }
    } catch (error: any) {
      console.error('Failed to add user:', error);
      setMessage({ type: 'error', text: error.message || 'ユーザーの追加に失敗しました' });
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.updateTenantUser(selectedUser.id, {
        name: selectedUser.name,
        role: selectedUser.role
      }) as any;
      
      if (response.success) {
        // ユーザー一覧を再読み込み
        await loadUsers();
        setMessage({ type: 'success', text: response.message || 'ユーザー情報を更新しました' });
        setEditDialogOpen(false);
        setSelectedUser(null);
      } else {
        throw new Error(response.error || 'ユーザー更新に失敗しました');
      }
    } catch (error: any) {
      console.error('Failed to update user:', error);
      setMessage({ type: 'error', text: error.message || 'ユーザー情報の更新に失敗しました' });
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return '管理者';
      case 'manager': return 'マネージャー';
      case 'viewer': return '閲覧者';
      case 'user': return 'ユーザー'; // 後方互換性のため
      default: return role;
    }
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
    switch (role) {
      case 'admin': return 'primary';
      case 'manager': return 'secondary';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: '名前', width: 150 },
    { field: 'email', headerName: 'メールアドレス', width: 250 },
    {
      field: 'role',
      headerName: 'ロール',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value)}
          size="small"
          color={getRoleColor(params.value)}
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'ステータス',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'アクティブ' : '非アクティブ'}
          size="small"
          color={params.value ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'last_login_at',
      headerName: '最終ログイン',
      width: 150,
      valueFormatter: (params) =>
        params.value
          ? new Date(params.value).toLocaleDateString('ja-JP')
          : '未ログイン',
    },
    {
      field: 'created_at',
      headerName: '作成日',
      width: 150,
      valueFormatter: (params) =>
        new Date(params.value).toLocaleDateString('ja-JP'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'アクション',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="編集"
          onClick={() => {
            setSelectedUser(params.row);
            setEditDialogOpen(true);
          }}
        />,
        <GridActionsCellItem
          icon={params.row.is_active ? <BlockIcon /> : <CheckCircleIcon />}
          label={params.row.is_active ? '無効化' : '有効化'}
          onClick={() => handleToggleUserStatus(params.row.id, params.row.is_active)}
        />,
      ],
    },
  ];

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          ユーザー一覧 ({users.length}名)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddUserDialogOpen(true)}
        >
          ユーザー追加
        </Button>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="名前、メールアドレスで検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Paper>

      {/* ユーザー追加ダイアログ */}
      <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー追加</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="名前"
              value={addUserName}
              onChange={(e) => setAddUserName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="メールアドレス"
              type="email"
              value={addUserEmail}
              onChange={(e) => setAddUserEmail(e.target.value)}
              sx={{ mb: 2 }}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="パスワード"
              type="password"
              value={addUserPassword}
              onChange={(e) => setAddUserPassword(e.target.value)}
              sx={{ mb: 2 }}
              required
              helperText="8文字以上で設定してください"
            />
            <FormControl fullWidth>
              <InputLabel>ロール</InputLabel>
              <Select
                value={addUserRole}
                label="ロール"
                onChange={(e) => setAddUserRole(e.target.value as any)}
              >
                <MenuItem value="viewer">閲覧者</MenuItem>
                <MenuItem value="manager">マネージャー</MenuItem>
                <MenuItem value="admin">管理者</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddUserDialogOpen(false);
            setAddUserEmail('');
            setAddUserName('');
            setAddUserPassword('');
            setAddUserRole('viewer');
          }}>
            キャンセル
          </Button>
          <Button 
            onClick={handleAddUser}
            variant="contained"
            disabled={!addUserEmail || !addUserName || !addUserPassword || addUserPassword.length < 8}
          >
            ユーザーを追加
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー編集</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="名前"
                value={selectedUser.name}
                onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="メールアドレス"
                value={selectedUser.email}
                onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                sx={{ mb: 2 }}
                disabled
              />
              <FormControl fullWidth>
                <InputLabel>ロール</InputLabel>
                <Select
                  value={selectedUser.role}
                  label="ロール"
                  onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value as any })}
                >
                  <MenuItem value="viewer">閲覧者</MenuItem>
                  <MenuItem value="manager">マネージャー</MenuItem>
                  <MenuItem value="admin">管理者</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleEditUser}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TenantUsers;